import { ProductPackagingType } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import SmallNumber from '@shared/components/content/small-number';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { Separator } from '@shared/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@shared/components/ui/sheet';
import { cn, formatGrams, formatNumber } from '@shared/lib/utils';
import ProductMetric from '../components/product-metric';
import { roundNumberToNDecimalPlaces } from '../../../../utils/helpers';

interface ProductDetailPackagingSheetProps
  extends React.HTMLAttributes<HTMLDivElement> {
  product: string;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  packagingType: ProductPackagingType;
  totalEmissions: number;
}

export default function ProductDetailPackagingSheet({
  className,
  isOpen,
  setIsOpen,
  packagingType,
  product,
  totalEmissions,
  ...props
}: ProductDetailPackagingSheetProps) {
  const itemEmissions = packagingType.totalEmissionsPerItem;
  const shareOfTotalEmissions =
    (packagingType.totalEmissionsPerItem / totalEmissions) * 100;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        className={cn('flex h-full flex-col sm:max-w-2xl', className)}
        {...props}
      >
        <SheetHeader className="-mt-1 space-y-0 border-b pb-4">
          <Text>{product}</Text>
          <SheetTitle className="text-xl font-semibold">
            {packagingType.name}
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 space-y-3 overflow-y-auto">
          <div className="flex">
            <ProductMetric
              title="Item emissions"
              className="flex-1 space-y-0"
              value={formatNumber(itemEmissions)}
              unit="kgCO2e"
              valueClassName="text-2xl"
            />
            <ProductMetric
              title="Share of total emissions"
              className="flex-1 space-y-0"
              value={formatNumber(shareOfTotalEmissions)}
              unit="%"
              valueClassName="text-2xl"
            />
          </div>

          <Separator />
          <div className="space-y-4">
            <div className="space-y-2">
              <Text variant="subSection">Packaging modelling</Text>
              <Text className="font-light text-muted-foreground">
                This item is linked to a packaging item in your account.
              </Text>
            </div>

            <div>
              <Text className="font-semibold">Linked to</Text>
              <div className="flex items-center gap-1.5 ">
                <Icons.package size={16} />
                <Text className="">{packagingType.name}</Text>
              </div>
            </div>
          </div>

          {packagingType.totalEmissionsPerKg != null && (
            <div className="space-y-1">
              <Text className="font-semibold">Carbon intensity</Text>
              <div className="flex items-center gap-1">
                <div className="flex items-baseline gap-1">
                  <Text className="text-2xl font-medium text-primary-600">
                    {formatNumber(packagingType.totalEmissionsPerKg)}
                  </Text>
                  <Unit variant="kgCO2e/kg" className="text-xs" />
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_5rem_6rem_5rem_6rem] items-start border-b pb-1.5">
              <Text className="text-xs font-semibold">Component</Text>
              <Text className="text-nowrap text-right text-xs font-semibold">
                % recycled
              </Text>
              <div className="text-right">
                <Text className="text-xs font-semibold">
                  Products contained
                </Text>
              </div>
              <div className="text-right">
                <Text className="text-xs font-semibold">Weight</Text>
                <Unit
                  variant="g"
                  className="block text-[10px] text-muted-foreground"
                />
              </div>
              <div className="text-right">
                <Text className="text-xs font-semibold">Emissions</Text>
                <Unit
                  variant="kgCO2e"
                  className="block text-[10px] text-muted-foreground"
                />
              </div>
            </div>
            {packagingType.items.map((item) => (
              <div
                key={item.description}
                className="grid grid-cols-[1fr_5rem_6rem_5rem_6rem] items-center"
              >
                <Text className="line-clamp-2 truncate text-sm text-muted-foreground">
                  {item.description}
                </Text>
                <Text className="text-right text-sm text-muted-foreground">
                  {item.proportionFromRecycledSources != null
                    ? `${roundNumberToNDecimalPlaces(
                        item.proportionFromRecycledSources * 100,
                        1,
                        item.proportionFromRecycledSources * 100 !== 100
                      )}%`
                    : '—'}
                </Text>
                <Text className="text-right text-sm text-muted-foreground">
                  {item.numberOfItemsContained}
                </Text>
                <Text className="text-right text-sm text-muted-foreground">
                  <SmallNumber
                    value={item.weight * 1000}
                    formatter={formatGrams}
                  />
                </Text>
                <Text className="text-right text-sm text-muted-foreground">
                  {formatNumber(item.totalEmissionsPerItem)}
                </Text>
              </div>
            ))}
          </div>
        </div>

        <div className="shrink-0 pt-3">
          <Text className="text-xs text-muted-foreground">
            This information is based on a snapshot of the data at the time of
            the last assessment.
          </Text>
        </div>
      </SheetContent>
    </Sheet>
  );
}
